import React, { useState, useEffect } from 'react';
import './NewsManager.css';
import './HomeAdmin.css';
import AdminNavbar from './AdminNavbar';

const API_URL = 'https://backend-zonamicroondas.vercel.app/api';
const PAGE_SIZE = 5; // <-- mostrar 5 noticias por página

const NewsManager = () => {

  /* ====== ESTADOS ORIGINALES ====== */
  const [view, setView] = useState('list'); // 'list' o 'create'
  const [news, setNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Paginación
  const [page, setPage] = useState(0);

  // Estado del formulario
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    summary: '',
    author_id: '',
    main_category_id: '',
    status: 'draft',
    is_featured: 0,
    published_at: '',
    tags: []
  });

  // Bloques de contenido (texto, embed de Twitter/X, etc.)
  const [blocks, setBlocks] = useState([]);

  const addBlock = (type) => {
    setBlocks(prev => [...prev, { id_temp: Date.now(), type, content: '' }]);
  };

  const removeBlock = (id_temp) => {
    setBlocks(prev => prev.filter(b => b.id_temp !== id_temp));
  };

  const updateBlock = (id_temp, content) => {
    setBlocks(prev => prev.map(b => b.id_temp === id_temp ? { ...b, content } : b));
  };

  const moveBlock = (index, dir) => {
    setBlocks(prev => {
      const arr = [...prev];
      const next = index + dir;
      if (next < 0 || next >= arr.length) return arr;
      [arr[index], arr[next]] = [arr[next], arr[index]];
      return arr;
    });
  };

  // Estado para imágenes
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);

  // Estado de edición
  const [editingId, setEditingId] = useState(null);

  // Cargar datos iniciales
  useEffect(() => {
    loadNews();
    loadCategories();
    loadAuthors();
    loadTags();
  }, []);

  const loadNews = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/news?include_scheduled=true`);
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        // ordenar por fecha descendente
        const sorted = data.data.slice().sort((a, b) => {
          const da = new Date(a.published_at || a.created_at || 0).getTime();
          const db = new Date(b.published_at || b.created_at || 0).getTime();
          return db - da;
        });
        setNews(sorted);
        setPage(0); // reiniciar a la primera página
      } else {
        setNews([]);
        setPage(0);
      }
    } catch (err) {
      setError('Error al cargar noticias: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/categories`);
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setCategories(data.data);
      }
    } catch (err) {
      console.error('Error al cargar categorías:', err);
    }
  };

  const loadAuthors = async () => {
    try {
      const response = await fetch(`${API_URL}/authors`);
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setAuthors(data.data);
      }
    } catch (err) {
      console.error('Error al cargar autores:', err);
    }
  };

  const loadTags = async () => {
    try {
      const response = await fetch(`${API_URL}/tags`);
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setTags(data.data);
      }
    } catch (err) {
      console.error('Error al cargar tags:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  const handleTagToggle = (tagId) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tagId)
        ? prev.tags.filter(id => id !== tagId)
        : [...prev.tags, tagId]
    }));
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedImages(files);

    // Crear previsualizaciones
    const previews = files.map(file => URL.createObjectURL(file));
    setImagePreviews(previews);
  };

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const toDateTimeLocal = (isoString) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    const pad = (n) => n.toString().padStart(2, '0');
    const year = d.getFullYear();
    const month = pad(d.getMonth() + 1);
    const day = pad(d.getDate());
    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Helper: subir imágenes (reutilizable para create/update)
  const uploadImages = async (newsId) => {
    if (!newsId) throw new Error('newsId inválido para subir imágenes');
    if (!selectedImages || selectedImages.length === 0) return;

    for (let i = 0; i < selectedImages.length; i++) {
      const formDataImg = new FormData();
      formDataImg.append('image', selectedImages[i]);
      formDataImg.append('position', i);
      formDataImg.append('alt_text', `Imagen ${i + 1} de ${formData.title}`);

      const res = await fetch(`${API_URL}/news/${newsId}/images`, {
        method: 'POST',
        body: formDataImg
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`Error uploading image ${i}: ${res.status} ${txt}`);
      }

      const data = await res.json().catch(() => null);
      if (!data || !data.success) {
        throw new Error(data?.error || `Error al subir imagen ${i}`);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const blocksPayload = blocks.map((b, i) => ({ type: b.type, content: b.content, position: i }));

      // Si está publicada, asignar fecha solo la primera vez (draft → published)
      const payload = { ...formData, blocks: blocksPayload };
      if (payload.status === 'published') {
        if (!editingId || formData.status !== 'published') {
          const d = new Date();
          d.setSeconds(d.getSeconds() - 10);
          payload.published_at = d.toISOString();
        }
      } else {
        payload.published_at = null;
      }

      if (editingId) {
        // Actualizar noticia
        const updateResponse = await fetch(`${API_URL}/news/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const updateData = await updateResponse.json();
        if (!updateData.success) throw new Error(updateData.error || 'Error al actualizar noticia');

        // Subir nuevas imágenes si hay
        if (selectedImages.length > 0) {
          await uploadImages(editingId);
        }

        setSuccess('Noticia actualizada correctamente');
      } else {
        // Crear nueva
        const newsResponse = await fetch(`${API_URL}/news`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const newsData = await newsResponse.json();
        if (!newsData.success) throw new Error(newsData.error || 'Error al crear noticia');

        const newsId = newsData.data.id;

        // Subir imágenes
        if (selectedImages.length > 0) {
          await uploadImages(newsId);
        }

        setSuccess('¡Noticia creada exitosamente!');
      }

      // Resetear formulario
      setFormData({
        title: '',
        subtitle: '',
        summary: '',
        author_id: '',
        main_category_id: '',
        status: 'draft',
        is_featured: 0,
        published_at: '',
        tags: []
      });
      setBlocks([]);
      setSelectedImages([]);
      setImagePreviews([]);
      setEditingId(null);

      // Recargar noticias y volver a la vista de lista
      await loadNews();
      setTimeout(() => {
        setView('list');
        setSuccess(null);
      }, 1200);

    } catch (err) {
      setError('Error: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No publicada';
    return new Date(dateString).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  /* ========== NUEVOS HANDLERS: EDITAR / ELIMINAR ========== */
  const handleEdit = async (newsId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/news/${newsId}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'No se pudo obtener la noticia');

      const n = data.data;
      // Mapear tags (si vienen como objetos)
      let tagIds = [];
      if (Array.isArray(n.tags)) {
        tagIds = n.tags.map(t => {
          if (typeof t === 'number') return t;
          if (t && (t.id || t.tag_id)) return t.id || t.tag_id;
          if (t && t.tags && t.tags.id) return t.tags.id;
          return null;
        }).filter(Boolean);
      }

      setFormData({
        title: n.title || '',
        subtitle: n.subtitle || '',
        summary: n.summary || '',
        author_id: n.author_id || '',
        main_category_id: n.main_category_id || '',
        status: n.status || 'draft',
        is_featured: n.is_featured ? 1 : 0,
        published_at: toDateTimeLocal(n.published_at),
        tags: tagIds
      });

      // Cargar bloques existentes
      setBlocks((n.blocks || []).map((b, i) => ({
        id_temp: Date.now() + i,
        type: b.type || 'text',
        content: b.content || ''
      })));

      // Previsualizaciones solo con las URLs existentes (no File objects)
      const previews = (n.images || []).map(img => img.url).filter(Boolean);
      setImagePreviews(previews);
      setSelectedImages([]);

      setEditingId(newsId);
      setView('create');

    } catch (err) {
      setError('Error al cargar noticia para editar: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (newsId) => {
    const ok = window.confirm('¿Eliminar esta noticia? Esta acción no se puede deshacer.');
    if (!ok) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${API_URL}/news/${newsId}`, { method: 'DELETE' });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Error al eliminar noticia');

      // Quitar de la UI y ajustar paginación
      setNews(prev => {
        const next = prev.filter(n => n.id !== newsId);
        // ajustar página si quedó fuera de rango
        const newTotalPages = Math.max(1, Math.ceil(next.length / PAGE_SIZE));
        setPage(p => Math.min(p, newTotalPages - 1));
        return next;
      });

      setSuccess('Noticia eliminada correctamente');

    } catch (err) {
      setError('Error al eliminar noticia: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormData({
      title: '',
      subtitle: '',
      summary: '',
      author_id: '',
      main_category_id: '',
      status: 'draft',
      is_featured: 0,
      published_at: '',
      tags: []
    });
    setBlocks([]);
    setSelectedImages([]);
    setImagePreviews([]);
  };

  // ---------- PAGINACIÓN ----------
  const totalPages = Math.max(1, Math.ceil(news.length / PAGE_SIZE));
  const start = page * PAGE_SIZE;
  const pageItems = news.slice(start, start + PAGE_SIZE);

  const handlePrev = () => setPage(p => Math.max(0, p - 1));
  const handleNext = () => setPage(p => Math.min(totalPages - 1, p + 1));

  // ---------- RENDER ----------
  return (
    <div className="home-container">
      <AdminNavbar />

      {/* Contenido principal */}
      <main className="main-content">
        <div className="news-manager">
          <div className="news-manager-header">
            <h2>Gestión de Noticias</h2>
            <div className="view-buttons">
              <button
                className={`btn ${view === 'list' ? 'btn-active' : 'btn-secondary'}`}
                onClick={() => setView('list')}
              >
                Ver Todas
              </button>
              <button
                className={`btn ${view === 'create' ? 'btn-active' : 'btn-secondary'}`}
                onClick={() => { setView('create'); cancelEdit(); }}
              >
                Crear Nueva
              </button>
            </div>
          </div>

          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          {view === 'list' ? (
            <div className="news-list-container">
              {loading ? (
                <div className="loading">Cargando noticias...</div>
              ) : (
                <div className="table-responsive">
                  <table className="news-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Imagen</th>
                        <th>Título</th>
                        <th>Categoría</th>
                        <th>Autor</th>
                        <th>Estado</th>
                        <th>Destacada</th>
                        <th>Fecha Publicación</th>
                        <th>Creada</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageItems.length === 0 ? (
                        <tr>
                          <td colSpan="10" className="no-data">No hay noticias registradas</td>
                        </tr>
                      ) : (
                        pageItems.map(item => (
                          <tr key={item.id}>
                            <td>{item.id}</td>
                            <td>
                              {item.image_url ? (
                                <img
                                  src={item.image_url}
                                  alt={item.title}
                                  className="table-image"
                                />
                              ) : (
                                <div className="no-image">Sin imagen</div>
                              )}
                            </td>
                            <td className="title-cell">{item.title}</td>
                            <td>{item.category_name || 'Sin categoría'}</td>
                            <td>{item.author_name || 'Sin autor'}</td>
                            <td>
                              <span className={`status-badge status-${item.status}`}>
                                {item.status === 'published' ? 'Publicada' : item.status === 'draft' ? 'Borrador' : item.status === 'archived' ? 'Archivada' : item.status}
                              </span>
                            </td>
                            <td className="centered">
                              {item.is_featured ? '⭐' : '-'}
                            </td>
                            <td>{formatDate(item.published_at)}</td>
                            <td>{formatDate(item.created_at)}</td>
                            <td>
                              <button className="btn btn-small btn-edit" onClick={() => handleEdit(item.id)}>Editar</button>
                              <button className="btn btn-small btn-danger" onClick={() => handleDelete(item.id)}>Eliminar</button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>

                  {/* PAGINADOR (solo cuando hay más de una página) */}
                  {news.length > PAGE_SIZE && (
                    <div className="pagination" style={{
                      marginTop: 16,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 12
                    }}>
                      <button
                        className="page-btn"
                        onClick={handlePrev}
                        disabled={page === 0}
                        aria-label="Anterior"
                      >
                        &lt;
                      </button>

                      <div className="page-info" aria-live="polite" style={{ minWidth: 160, textAlign: 'center' }}>
                        Noticias {page + 1} de {totalPages}
                      </div>

                      <button
                        className="page-btn"
                        onClick={handleNext}
                        disabled={page >= totalPages - 1}
                        aria-label="Siguiente"
                      >
                        &gt;
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="news-create-container">
              <form onSubmit={handleSubmit} className="news-form">
                <div className="form-section">
                  <h3>Información Básica</h3>

                  <div className="form-group">
                    <label htmlFor="title">Título *</label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      placeholder="Título de la noticia"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="subtitle">Subtítulo</label>
                    <input
                      type="text"
                      id="subtitle"
                      name="subtitle"
                      value={formData.subtitle}
                      onChange={handleInputChange}
                      placeholder="Subtítulo de la noticia"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="summary">Resumen *</label>
                    <textarea
                      id="summary"
                      name="summary"
                      value={formData.summary}
                      onChange={handleInputChange}
                      required
                      rows="4"
                      placeholder="Resumen breve de la noticia"
                    />
                  </div>
                </div>

                <div className="form-section">
                  <h3>Clasificación</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="author_id">Autor *</label>
                      <select
                        id="author_id"
                        name="author_id"
                        value={formData.author_id}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Selecciona un autor</option>
                        {authors.map(author => (
                          <option key={author.id} value={author.id}>
                            {author.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="main_category_id">Categoría *</label>
                      <select
                        id="main_category_id"
                        name="main_category_id"
                        value={formData.main_category_id}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Selecciona una categoría</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Etiquetas (Tags)</label>
                    <div className="tags-container">
                      {tags.map(tag => (
                        <label key={tag.id} className="tag-checkbox">
                          <input
                            type="checkbox"
                            checked={formData.tags.includes(tag.id)}
                            onChange={() => handleTagToggle(tag.id)}
                          />
                          <span>{tag.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Publicación</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="status">Estado</label>
                      <select
                        id="status"
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                      >
                        <option value="draft">Borrador</option>
                        <option value="published">Publicada</option>
                        <option value="archived">Archivada</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="is_featured"
                        checked={formData.is_featured === 1}
                        onChange={handleInputChange}
                      />
                      <span>Marcar como destacada</span>
                    </label>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Cuerpo de la Nota</h3>
                  <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
                    Añade párrafos de texto o embeds de Twitter/X al cuerpo del artículo. Puedes reordenarlos con ↑ ↓.
                  </p>

                  <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => addBlock('text')}>
                      + Párrafo de texto
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={() => addBlock('embed')}
                      style={{ borderColor: '#1da1f2', color: '#1da1f2' }}>
                      + Tweet / Embed
                    </button>
                  </div>

                  {blocks.length === 0 && (
                    <p style={{ color: '#aaa', fontSize: 13, fontStyle: 'italic' }}>
                      Sin bloques. El resumen se usará como contenido del artículo.
                    </p>
                  )}

                  {blocks.map((block, index) => (
                    <div key={block.id_temp} style={{
                      border: block.type === 'embed' ? '1px solid #1da1f2' : '1px solid #ddd',
                      borderRadius: 8,
                      padding: '12px 14px',
                      marginBottom: 12,
                      background: block.type === 'embed' ? '#f0f8ff' : '#fafafa'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontWeight: 600, fontSize: 13, color: block.type === 'embed' ? '#1da1f2' : '#555' }}>
                          {block.type === 'embed' ? '🐦 Tweet / Embed' : '📝 Párrafo'} #{index + 1}
                        </span>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button type="button" className="btn btn-secondary" style={{ padding: '2px 8px', fontSize: 13 }}
                            onClick={() => moveBlock(index, -1)} disabled={index === 0} title="Subir">↑</button>
                          <button type="button" className="btn btn-secondary" style={{ padding: '2px 8px', fontSize: 13 }}
                            onClick={() => moveBlock(index, 1)} disabled={index === blocks.length - 1} title="Bajar">↓</button>
                          <button type="button" className="btn btn-danger" style={{ padding: '2px 8px', fontSize: 13 }}
                            onClick={() => removeBlock(block.id_temp)}>✕</button>
                        </div>
                      </div>

                      {block.type === 'embed' ? (
                        <>
                          <textarea
                            value={block.content}
                            onChange={e => updateBlock(block.id_temp, e.target.value)}
                            rows={6}
                            placeholder={'Pega aquí el código del tweet, por ejemplo:\n<blockquote class="twitter-tweet"><p lang="es" ...>...</p>&mdash; Usuario <a href="...">fecha</a></blockquote>\n<script async src="https://platform.twitter.com/widgets.js" charset="utf-8"></script>'}
                            style={{ width: '100%', fontFamily: 'monospace', fontSize: 12, boxSizing: 'border-box', borderRadius: 6, border: '1px solid #b3d4f5', padding: 8 }}
                          />
                          <small style={{ color: '#666', display: 'block', marginTop: 4 }}>
                            Copia el código "Embed Tweet" desde Twitter/X y pégalo aquí. El script se incluye automáticamente.
                          </small>
                        </>
                      ) : (
                        <textarea
                          value={block.content}
                          onChange={e => updateBlock(block.id_temp, e.target.value)}
                          rows={4}
                          placeholder="Escribe el texto de este párrafo..."
                          style={{ width: '100%', boxSizing: 'border-box', borderRadius: 6, border: '1px solid #ddd', padding: 8 }}
                        />
                      )}
                    </div>
                  ))}
                </div>

                <div className="form-section">
                  <h3>Imágenes</h3>

                  <div className="form-group">
                    <label htmlFor="images" className="file-label">
                      Seleccionar Imágenes
                    </label>
                    <input
                      type="file"
                      id="images"
                      multiple
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="file-input"
                    />
                    <p className="help-text">
                      Puedes seleccionar múltiples imágenes. La primera será la imagen principal.
                    </p>
                  </div>

                  {imagePreviews.length > 0 && (
                    <div className="image-previews">
                      {imagePreviews.map((preview, index) => (
                        <div key={index} className="preview-item">
                          <img src={preview} alt={`Preview ${index + 1}`} />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="remove-image"
                          >
                            ✕
                          </button>
                          {index === 0 && <span className="main-badge">Principal</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    onClick={() => { if (editingId) { cancelEdit(); setView('list'); } else { setView('list'); } }}
                    className="btn btn-secondary"
                    disabled={loading}
                  >
                    {editingId ? 'Cancelar edición' : 'Cancelar'}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (editingId ? 'Actualizando...' : 'Creando...') : (editingId ? 'Actualizar Noticia' : 'Crear Noticia')}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-links">
          <a href="#privacidad">Política de Privacidad</a>
          <a href="#terminos">Términos y Condiciones</a>
          <a href="#contacto">Contacto</a>
        </div>
        <p className="footer-text">© 2025 Panel de Administrador. Todos los derechos reservados.</p>
      </footer>
    </div>
  );
};

export default NewsManager;
